import { useMemo } from 'react';

import { cn, ConditionalRender, Skeleton } from '@crossnokaye/ux-common';

import {
  AnimaticValueShelfItem,
  AnimaticValueShelfItemVariant,
  ValuePosition,
} from '@repo/sdk/models/components';

import { useGetRefValue } from '@/features/equipment-browser/subscriptions/use-get-ref-value';
import { formatUnits } from '@/helpers/unit-formatter/unit-formatter';
import { Inline } from '@/ux-common/components/inline/inline';
import { Stack } from '@/ux-common/components/stack/stack';

type ShelfValueSectionProps = {
  isMobile?: boolean;
  value: AnimaticValueShelfItem;
  isStatus?: boolean;
};

const valueItemVariants: Record<AnimaticValueShelfItemVariant, string> = {
  stage: 'bg-ckds-base-white rounded p-0.5 text-background',
  name: 'bg-ckds-neutral-200 rounded p-0.5 text-background',
  default: 'text-foreground',
  not_in_sequence: 'text-foreground py-3',
};

export const ShelfValueSection = ({ value, isStatus = false }: ShelfValueSectionProps) => {
  const variant = value.variant ?? AnimaticValueShelfItemVariant.Default;

  const refValue = useGetRefValue(value.ref);

  const displayValue = useMemo(() => {
    let display = value.value ?? '--';
    if (refValue != null) {
      const formattedLabels = formatUnits({
        value: refValue,
        units: value.ref?.units,
      });
      display = formattedLabels.valueUnitsLabel;
    }

    if (value.valueFormatter && display) {
      display = value.valueFormatter.replace('%s', display);
    }
    return display;
  }, [refValue, value]);

  return (
    <Inline className={'inline-row-sm flex-nowrap items-center'}>
      <ConditionalRender condition={isStatus}>
        <span className="text-sm font-bold text-muted-foreground leading-5 @2xl:text-sm">
          {'STATUS:'}
        </span>
      </ConditionalRender>
      <span
        className={cn(
          valueItemVariants[variant],
          'text-sm font-bold leading-5 @2xl:text-sm truncate uppercase'
        )}
      >
        {displayValue}
      </span>
    </Inline>
  );
};

type AnimaticShelfValuesProps = {
  values: AnimaticValueShelfItem[];
  isMobile: boolean;
  isStatus?: boolean;
};

const ValueShelfSection = ({ values, isMobile, isStatus }: AnimaticShelfValuesProps) => {
  return (
    <Inline className="inline-row-sm flex-nowrap">
      {values
        .filter((value) => !value.hidden)
        .map((value, index) => (
          <ShelfValueSection
            key={value.ref?.id ?? `shelf-value-${index}`}
            value={value}
            isMobile={isMobile}
            isStatus={isStatus}
          />
        ))}
    </Inline>
  );
};

export const AnimaticShelfValues = ({
  values,
  isMobile,
}: AnimaticShelfValuesProps): React.ReactNode => {
  const topValues = values.filter((value) => value.valuePosition === ValuePosition.Top);
  const bottomValues = values.filter((value) => value.valuePosition === ValuePosition.Bottom);
  return (
    <Stack className="stack-thin rounded-md border-2 border-tertiary p-1 @2xl:p-0.5 overflow-x-auto">
      <ValueShelfSection values={topValues} isMobile={isMobile} />
      <ValueShelfSection values={bottomValues} isMobile={isMobile} isStatus={true} />
    </Stack>
  );
};

export const ValueShelfSkeletons = () => {
  return <Skeleton className="w-full h-4" />;
};
